import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  FormGroup,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useGetChannels } from "api/channels/service";
import { IoMdCloseCircle } from "react-icons/io";
import { MdSearch } from "react-icons/md";
import IdentityInformationForm from "../../../components/Customers/IdentityInformationForm";
import { useCreateCustomer } from "api/customers/service";
import { formatDateForAPI } from "utils/dateUtils";

const InventoryRightDrawer = ({
  isOpen,
  onClose,
  type,
  onSelect,
  activeColorStyle,
  isLightTheme,
  initialData,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data: channelsData, isLoading } = useGetChannels(
    type === "individual" ? null : type,
    page,
    pageSize,
    debouncedSearchTerm
  );

  const createCustomerMutation = useCreateCustomer({ onClose: () => { } });

  const [identityData, setIdentityData] = useState({
    ssn: "",
    date_of_birth: "",
    full_name: "",
    gender: "",
    active_service_member: false,
    new_customer: true,
    customer_id: null,
  });

  useEffect(() => {
    if (isOpen && initialData && type === "individual") {
      setIdentityData({
        ssn: initialData.ssn || "",
        date_of_birth: initialData.date_of_birth || "",
        full_name: initialData.full_name || "",
        gender: initialData.gender || "",
        active_service_member: !!initialData.active_service_member,
        new_customer: initialData.new_customer !== undefined ? initialData.new_customer : !(initialData.customer_id || initialData.id),
        customer_id: initialData.customer_id || initialData.id || (typeof initialData === 'object' ? null : initialData),
      });
    } else if (isOpen && type === "individual") {
      setIdentityData({
        ssn: "",
        date_of_birth: "",
        full_name: "",
        gender: "",
        active_service_member: false,
        new_customer: true,
        customer_id: null,
      });
    }
  }, [isOpen, initialData, type]);

  const handleIdentityInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setIdentityData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value
    }));
  };

  const handleCustomerSubmit = (data = identityData) => {
    if (data.new_customer) {
      const formattedData = {
        ...data,
        date_of_birth: formatDateForAPI(data.date_of_birth)
      };
      createCustomerMutation.mutate(formattedData, {
        onSuccess: (response) => {
          // Note: In this project's service, the first arg of onSuccess is the response data
          const customerData = response?.data || response;
          const newId = customerData.id || customerData.customer_id;
          const finalData = { ...formattedData, customer_id: newId, new_customer: false };
          onSelect(finalData);
          onClose();
        }
      });
    } else {
      onSelect(data);
      onClose();
    }
  };

  const results = useMemo(() => {
    return channelsData?.results || [];
  }, [channelsData]);

  const drawerStyles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 10000,
        display: isOpen ? "block" : "none",
        opacity: isOpen ? 1 : 0,
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "fixed",
        top: 0,
        right: 0,
        width: "40vw",
        maxWidth: "90vw",
        height: "100vh",
        backgroundColor: isLightTheme ? "#fff" : "#1e1e2f",
        color: isLightTheme ? "#1e1e2f" : "#fff",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        zIndex: 10001,
        display: "flex",
        flexDirection: "column",
        transform: isOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 300ms ease",
      },
      header: {
        padding: "16px",
        borderBottom: isLightTheme
          ? "1px solid rgba(0,0,0,0.1)"
          : "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      },
      body: {
        padding: "16px",
        overflow: "auto",
        flex: 1,
      },
    }),
    [isOpen, isLightTheme]
  );

  return (
    <>
      <div style={drawerStyles.overlay} onClick={onClose} />
      <div style={drawerStyles.panel}>
        <div style={drawerStyles.header}>
          <h4 className="mb-0">
            {type === "individual"
              ? t("inventoryDrawer.selectCustomer")
              : t("inventoryDrawer.selectChannel")}
          </h4>
          <div className="d-flex align-items-center">
            {!identityData.new_customer && (
              <Button
                color="info"
                size="sm"
                className="mr-3"
                onClick={() => setIdentityData({
                  ssn: "",
                  date_of_birth: "",
                  full_name: "",
                  gender: "",
                  active_service_member: false,
                  new_customer: true,
                  customer_id: null,
                })}
              >
                {t("inventoryDrawer.createNew")}
              </Button>
            )}
            <IoMdCloseCircle
              size={30}
              style={{ cursor: "pointer", opacity: 0.7 }}
              onClick={onClose}
            />
          </div>
        </div>
        <div style={drawerStyles.body}>
          {type === "individual" ? (
            <div className="p-2">
              <IdentityInformationForm
                identityData={identityData}
                onChange={handleIdentityInputChange}
                showValidation={false}
                handleSubmit={() => handleCustomerSubmit()}
                onCustomerSelect={(idOrCustomer, customerObj) => {
                  const customer = customerObj || idOrCustomer;
                  const id = typeof customer === 'object' ? (customer.id || customer.customer_id) : idOrCustomer;

                  const selectedData = {
                    ...identityData,
                    new_customer: false,
                    customer_id: id,
                    full_name: customer?.full_name || identityData.full_name,
                    ssn: customer?.ssn || identityData.ssn,
                    date_of_birth: customer?.date_of_birth || identityData.date_of_birth,
                    gender: customer?.gender || identityData.gender,
                  };
                  handleCustomerSubmit(selectedData);
                }}
                activeColor={activeColorStyle?.backgroundColor || "blue"}
              />
              <div className="mt-4">
                <Button
                  block
                  style={{ ...activeColorStyle, color: 'white' }}
                  onClick={() => handleCustomerSubmit()}
                  disabled={!identityData.full_name || createCustomerMutation.isPending}
                >
                  {createCustomerMutation.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    t("inventoryDrawer.useThisCustomer")
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <FormGroup className="mb-4">
                <Input
                  placeholder={t("inventoryDrawer.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ borderRadius: 0 }}
                />
              </FormGroup>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  <Table responsive hover className="table-shopping">
                    <thead>
                      <tr>
                        <th>{t("inventoryDrawer.channelName")}</th>
                        <th className="text-right">{t("inventoryDrawer.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.length > 0 ? (
                        results.map((channel) => (
                          <tr
                            key={channel.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              onSelect(channel);
                              onClose();
                            }}
                          >
                            <td>
                              <div className="font-weight-bold">{channel.name}</div>
                              {channel.type && (
                                <small className="text-muted">
                                  {t(`channels.types.${channel.type}`, { defaultValue: channel.type })}
                                </small>
                              )}
                            </td>
                            <td className="text-right">
                              <Button
                                color="link"
                                style={{ ...activeColorStyle, padding: '5px 10px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelect(channel);
                                  onClose();
                                }}
                              >
                                {t("inventoryDrawer.select")}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center py-4">
                            {t("inventoryDrawer.noResults")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {channelsData?.pagination && channelsData.pagination.pageCount > 1 && (
                    <div className="d-flex justify-content-center mt-3 mb-4 flex-wrap">
                      <Button
                        size="sm"
                        style={{
                          borderRadius: '0px !important',
                          margin: '2px',
                          padding: '8px 12px',
                          backgroundColor: page <= 1 ? 'rgba(255,255,255,0.05)' : (activeColorStyle?.backgroundColor || '#1d8cf8'),
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff !important',
                          opacity: page <= 1 ? 0.3 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '40px'
                        }}
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <i className="tim-icons icon-minimal-left" style={{ color: '#fff !important' }} />
                      </Button>
                      {[...Array(channelsData.pagination.pageCount)].map((_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          onClick={() => setPage(i + 1)}
                          style={{
                            borderRadius: '0px !important',
                            margin: '2px',
                            padding: '8px 12px',
                            backgroundColor: i + 1 === page ? (activeColorStyle?.backgroundColor || '#1d8cf8') : 'rgba(255,255,255,0.1)',
                            borderColor: i + 1 === page ? '#fff' : 'rgba(255,255,255,0.2)',
                            color: '#fff !important',
                            boxShadow: 'none',
                            minWidth: '40px',
                            fontWeight: i + 1 === page ? 'bold' : 'normal'
                          }}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        style={{
                          borderRadius: '0px !important',
                          margin: '2px',
                          padding: '8px 12px',
                          backgroundColor: page >= channelsData.pagination.pageCount ? 'rgba(255,255,255,0.05)' : (activeColorStyle?.backgroundColor || '#1d8cf8'),
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff !important',
                          opacity: page >= channelsData.pagination.pageCount ? 0.3 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '40px'
                        }}
                        disabled={page >= channelsData.pagination.pageCount}
                        onClick={() => setPage(page + 1)}
                      >
                        <i className="tim-icons icon-minimal-right" style={{ color: '#fff !important' }} />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default InventoryRightDrawer;
